import type { GPTModelItem } from "@/api/models"
import type { ReasoningEffortOverride } from "@/stores/ai-explorer-store"

const REASONING_EFFORTS = new Set<ReasoningEffortOverride>([
  "none",
  "minimal",
  "low",
  "medium",
  "high",
  "xhigh",
  "max",
])

export type WorkflowModelSettingsPatch = {
  model?: string
  reasoningEffort?: ReasoningEffortOverride
}

export function normalizeWorkflowModelId(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value : undefined
}

export function normalizeWorkflowReasoningEffort(
  value: unknown
): ReasoningEffortOverride | undefined {
  return typeof value === "string" && REASONING_EFFORTS.has(value as ReasoningEffortOverride)
    ? (value as ReasoningEffortOverride)
    : undefined
}

/**
 * Resolve what the picker should display without mutating a persisted legacy
 * node. Only a genuinely unconfigured legacy node receives Auto (matching the
 * Explorer and runtime contract), then the catalog default as a compatibility
 * fallback. An explicit removed id stays unresolved so the editor can show
 * the same fail-loud state the runtime enforces.
 */
export function resolveWorkflowModel(
  models: GPTModelItem[],
  persistedModelId: unknown
): GPTModelItem | undefined {
  const modelId = normalizeWorkflowModelId(persistedModelId)
  if (modelId) return models.find((model) => model.id === modelId)
  return (
    models.find((model) => model.id === "auto") ??
    models.find((model) => model.default) ??
    models[0]
  )
}

/** Only expose a stored effort when the displayed model supports it. */
export function resolveWorkflowEffortOverride(
  model: GPTModelItem | undefined,
  persistedEffort: unknown
): ReasoningEffortOverride | null {
  const effort = normalizeWorkflowReasoningEffort(persistedEffort)
  return effort && model?.supported_efforts?.includes(effort) ? effort : null
}

/**
 * Build one store patch for a user model pick. Keeping `reasoningEffort` in the
 * same object makes compatibility cleanup atomic; assigning `undefined` also
 * keeps the field optional when the workflow is serialized.
 */
export function createWorkflowModelChange(
  models: GPTModelItem[],
  nextModelId: string,
  persistedEffort: unknown
): WorkflowModelSettingsPatch {
  const nextModel = models.find((model) => model.id === nextModelId)
  const effort = normalizeWorkflowReasoningEffort(persistedEffort)
  const supportsEffort = Boolean(effort && nextModel?.supported_efforts?.includes(effort))

  return {
    model: nextModelId,
    reasoningEffort: supportsEffort ? effort : undefined,
  }
}
